// Transport-agnostic send. The active transport (ws.js / supabase.js / etc.)
// installs `window.__simoneSendOverBridge` during initBridge so this function
// doesn't have to know whether it's talking to a chrome.runtime port, a
// direct WebSocket, or a supabase realtime channel.

import { normalizeComposerImages, describeRejectedImages } from './attachments.js'

// sendPrompt(text, scope, images, attached)
//
// `images` (optional) — the composer's own `state.images` bag, in EITHER
// composer shape (see ./attachments.js). Normalized here into the envelope's
// `attachments` array; the transport forwards it to sdk.ai, the SDK posts it
// on the turn, and the server persists it as `image` content parts the
// multimodal providers send as real image blocks. Callers with no picker
// (Suggestions, DailyUpdate, homeAiSend, forceBuildApp) pass nothing and the
// envelope is byte-identical to what it was before this argument existed —
// `attachments` is OMITTED, never sent as an empty array.
// `attached` (optional) — the per-turn STRUCTURED attachment payload
// (`{ refs?, canvas? }`, spec §4.1) the canvas/home composer forwards from
// getSelectionAiContext(). It rides on the envelope's `context.attached`, and
// simoneAiTransport merges it with the persistent `root.aiAttached` bag before
// dispatch. Omitted from the envelope when the turn carries none, so a plain
// send is byte-identical to what it was — the ws/chrome bridge transports read
// `text` + `context` and ignore keys they don't know.
//
// Note this is NOT the same thing as the drawer chips: those live on root and
// the transport reads them itself. This parameter exists only for the surface
// that computes its attachment fresh per send (the canvas selection).
// `opts` (optional, fifth) — `{ mode }`: ONE ask's own output (homeAiSend's
// per-ask mode — the focus panel's Summarize / Build a board). It rides the
// envelope's `context.assistantMode`, which the transport prefers over the
// person's stored mode for this turn only. Omitted when absent.
// `opts.resume` (literal `true` only) — the turn RESUMES a failed-after-tools
// turn (ChatMessage's Continue chip). `text` stays the member's own word (the
// chip label); the flag rides `context.resume` and the server tells the model
// to pick up where it stopped without redoing the saved actions. Omitted when
// absent, so a plain send is byte-identical.
export const sendPrompt = function (text, scope, images, attached, opts) {
  const trimmed = (text || '').trim()
  if (!trimmed) return
  const root = this.state.root
  // Build mode (root._aiBuildMode — the composer '+' menu toggle, 2026-09-02
  // per Nika "toggle which runs this functionality and opens that generated
  // page after output"): an armed send from ANY surface routes into the
  // addapp build pipeline. This transport hop is the one place every
  // sidebar/drawer send passes through, so the flip here covers them all;
  // the canvas composer (freestyle transport) branches in its own
  // _sendPrompt. Not re-flipped when the caller already says 'addapp'
  // (forceBuildApp, the /add-app page itself — that page arms its own
  // watcher or IS the surface).
  if (root && root._aiBuildMode && scope !== 'addapp' && scope !== 'dashboard') {
    scope = 'addapp'
    try {
      this.call('watchAiBuildOutput')
    } catch (_) {}
  }
  // THE BUILDER'S OWN THREAD is a builder turn, whichever chat sent it. The
  // general drawer can hold it (its History lists every workspace thread, and
  // the builder's is one), but the server marks the builder surface PER TURN:
  // a 'general' send there went as `workspace-shell` and lost the builder's
  // free first change (persona round 2d, marcus at 390 px: 442/389/388
  // credits quoted). While the builder is on screen on that same thread, the
  // send goes as 'addapp' with the app — its rows land in the builder chat,
  // so the drawer gives the screen back to it.
  if (
    root &&
    (!scope || scope === 'general') &&
    this.call('addAppBuilderThreadOpen', root) === true
  ) {
    scope = 'addapp'
    if (root.assistantOpen) root.update({ assistantOpen: false }, { preventFetch: true })
    try {
      this.call('addAppBuilderChatReveal')
    } catch (_) {}
  }
  // Prime the reply-speech audio element INSIDE this user gesture — autoplay
  // policy blesses an element that already played, which lets the spoken
  // reply (ElevenLabs) start even though it lands seconds after the click.
  try {
    this.call('primeSpeakAudio')
  } catch (_) {}
  // /add-app runs simone in its own project-scoped context — its buffer lives
  // under _ax_* and the turn is dispatched project-scoped (the active extension).
  // The general drawer/composer pass scope 'general' → the bare keys (no-op).
  const addapp = scope === 'addapp'
  // dashboard ('_db_') = the / inline-thread surface — its own thread, workspace-
  // scoped like 'general' but key-namespaced so it never collides with the drawer.
  const k = addapp ? '_ax_' : scope === 'dashboard' ? '_db_' : ''
  const projectId = addapp ? root._addAppProjectId || null : null
  const id = `u-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`

  // Snapshot the currently-picked elements onto the user message itself, so
  // the chip-stack stays visible in the message bubble even after we clear
  // the picker area for the next turn.
  const picked = root.pickedElements || []
  const inspectedUrl = (picked.length && picked[picked.length - 1].url) || null
  const inspectedTitle = (picked.length && picked[picked.length - 1].title) || null
  const pageUrl = inspectedUrl || (window.location && window.location.href) || null
  const pageTitle = inspectedTitle || (document && document.title) || null

  // Image attachments for this turn. Normalized ONCE here, so the bubble and
  // the wire agree on exactly what was sent — a picture refused by the caps
  // must not appear as attached on a message the model never saw it on.
  const { attachments, rejected } = normalizeComposerImages(images)
  const rejectedNote = describeRejectedImages(rejected, (key) => this.call('polyglot', key))

  const userMsg = {
    id,
    role: 'user',
    text: trimmed,
    ts: Date.now(),
    picked: picked.length ? picked.slice() : null,
    pageUrl,
    pageTitle,
    // Name/mime/size only — the base64 stays out of the message buffer (which
    // is persisted per session), and the thumbnails the composer already
    // rendered are the user's visual record of what went.
    attachments: attachments.length
      ? attachments.map((a) => ({ name: a.name, mime: a.mime, size: a.size }))
      : null
  }
  const next = (root[k + 'chatMessages'] || []).concat(
    userMsg,
    // Say what was dropped, in the thread, at the moment it was dropped.
    rejectedNote ? [{ id: `sys-${id}`, role: 'system', text: rejectedNote, ts: Date.now() }] : []
  )
  // Turn off inspect/pick mode on send — the picked refs are snapshotted onto
  // the message above, so there's no reason to keep the page picker armed.
  // Mirrors togglePick's off-path (stop_pick to the page + picking:false).
  if (root.picking && typeof window.__ccSendToPage === 'function') {
    try {
      window.__ccSendToPage({ type: 'stop_pick' })
    } catch (_) {}
  }
  // Clear the picker context — the snapshot is now on the message bubble.
  root.update({
    [k + 'chatMessages']: next,
    chatInput: '',
    [k + 'chatTyping']: true,
    picking: false,
    pickedElements: []
  })

  const send =
    typeof window.__simoneSendOverBridge === 'function' ? window.__simoneSendOverBridge : null

  if (!send) {
    const errs = (root[k + 'chatMessages'] || []).concat({
      id: `err-${Date.now()}`,
      role: 'system',
      text: this.call('polyglot', 'workspace.simone.noBridge'),
      ts: Date.now()
    })
    root.update({ [k + 'chatMessages']: errs, [k + 'chatTyping']: false })
    return
  }

  // Second arg = live root handle for the sdk.ai transport
  // (simoneAiTransport): its install-time root can go stale across shell
  // remounts, which made assistant patches silently no-op (empty bubbles).
  // This root is resolved fresh at send time so it's always the rendering
  // tree. Passed OUTSIDE the envelope because DOMQL state can't be
  // serialized — the ws/chrome bridge transports take (envelope) only and
  // ignore extra args.
  send(
    {
      id,
      type: 'user_message',
      text: trimmed,
      // Scope routes the turn: 'addapp' dispatches project-scoped to the active
      // extension (projectId); 'general' stays workspace-scoped.
      scope: scope || 'general',
      projectId,
      context: {
        pickedElements: picked,
        url: pageUrl,
        title: pageTitle,
        ...(attached && typeof attached === 'object' ? { attached } : {}),
        ...(opts && typeof opts.mode === 'string' ? { assistantMode: opts.mode } : {}),
        ...(opts && opts.resume === true ? { resume: true } : {})
      },
      // Attached images, `{ name, mime, size, dataUrl }` each. Added ONLY when
      // the turn carries one, so a text-only envelope is unchanged — the ws /
      // chrome bridge transports read `text` + `context` and ignore unknown
      // keys, and simoneAiTransport forwards this one to sdk.ai.
      ...(attachments.length ? { attachments } : {})
    },
    { stateRoot: root }
  )

  // Sync the active session with the new message buffer (scoped).
  try {
    this.call('persistActiveSession', scope)
  } catch (_) {}
}
